import { EditNoteOutlined } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { memo, useEffect } from 'react';
import { usePostModifyItemRemarksMutation } from '../../../../../api/front-office/roomposting.api.rtk';
import { getColor } from '../../../../../assets/theme';
import LoadingComponent from '../../../../../components/animation/loading-component';
import useToast from '../../../../../hooks/useToast';
import { useRoomPostingContext } from '../../../containers/fo-room_posting.container';
import { RoomPostingModifyItemRemarks } from '../../../interface/room-posting-interface';

import ContainerTitle from '@components/ui/container-title';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import { itemEntryRemarksSchema } from '../../../interface/room-posting-schema-interface';

type FormValues = {
  itemRemarks?: string | null;
};

const FORoomPostingItemRemarks = ({
  selectedIDs,
  curRemarks,
  handleClose,
}: {
  selectedIDs: string;
  curRemarks: string;
  handleClose: () => void;
}) => {
  const { primary } = getColor();
  const { toast } = useToast();
  const { useProperty, ledgerRefetch } = useRoomPostingContext();

  useEffect(() => {
    if (curRemarks) {
      (async () => {
        try {
          setValue('itemRemarks', curRemarks);
        } catch (error) {
          console.log(error);
        }
      })();
    }
    return () => {};
  }, [curRemarks]);

  // API Modify Item Line Remarks
  const [postModifyItemRemarks, { isLoading: isFetching }] =
    usePostModifyItemRemarksMutation();

  const methods = useForm<FormValues>({
    resolver: yupResolver(itemEntryRemarksSchema),
    mode: 'onSubmit',
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = methods;

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    const { itemRemarks } = data;
    const postUpdateRemarks: RoomPostingModifyItemRemarks = {
      properties: [
        {
          property: {
            propertyID: useProperty,
            trans_list: [
              {
                identifier_list: selectedIDs,
                item_remarks: itemRemarks?.trim() || '',
              },
            ],
          },
        },
      ],
    };

    await postModifyItemRemarks(postUpdateRemarks)
      .unwrap()
      .then(() => {
        toast({
          severity: 'success',
          message: `Successfully updated item remarks.`,
        });
        ledgerRefetch();
        handleClose();
      })
      .catch((error) =>
        toast({
          severity: 'error',
          message: `Error occurred update item remarks: ${error}`,
        }),
      );
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        gap={1}
        sx={{ p: 3, width: '100%' }}
      >
        {isFetching && <LoadingComponent />}

        <Stack>
          <Stack pb={1}>
            <Stack
              sx={{
                color: primary,
                backgroundColor: '#f7f7fb',
                width: '60px',
                height: '60px',
                borderRadius: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <EditNoteOutlined sx={{ fontSize: 26 }} />
            </Stack>

            <ContainerTitle
              title='Item Entry Remarks'
              func={() => {}}
              titleStyle={{
                fontSize: '18px!important',
                fontWeight: '500',
              }}
            />
          </Stack>

          <InputFieldsController
            label='Remarks'
            name='itemRemarks'
            placeholder='Enter item entry remarks'
            control={control}
            errors={errors}
            multiline
            rows={4}
            helperText={`${watch('itemRemarks')?.length || 0}/100`}
          />

          <Stack alignItems='flex-end'>
            <Stack direction='row' gap={1}>
              <Button
                id='item-remarks-cancel'
                variant='outlined'
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button type='submit' variant='contained'>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  );
};

export default memo(FORoomPostingItemRemarks);
